import type { Hex } from 'viem';

import type { GaslessStatusResponse } from '../../utils/gasless/types';
import type { IntentsStatusResponse, IntentsSubmitReceipt } from '../../utils/intents/types';
import type { ChainHash } from '../../utils/models';
import {
  SerializableTransactionReceipt,
  SwapTransaction,
  Transaction,
  GaslessTransaction,
  IntentsTransaction,
  WrapTransaction,
  BatchSwapTransaction,
  SolanaSwapTransaction,
  CrossChainTransaction,
  CrossChainStatusReceipt,
  isNotObservedReceipt,
  isReplacedReceipt,
  isSerializableTransactionReceipt,
  isWalletCallFallbackReceipt,
} from './types';

/**
 * The real on-chain swap hash of a batch-swap (its `tx.hash` is an EIP-5792
 * wallet-calls ID). Present on the full receipt and, usually, on the
 * wallet-call fallback receipt.
 */
export const getBatchSwapOnChainHash = (raw: BatchSwapTransaction['raw']): Hex | undefined =>
  isSerializableTransactionReceipt(raw) || isWalletCallFallbackReceipt(raw)
    ? raw.transactionHash
    : undefined;

/**
 * Resolves the mined on-chain transaction hash.
 *
 * Intents and gasless transactions use `tx.hash` as their stable store key,
 * which is the off-chain tradeHash. Their mined txHash is stored in `raw`.
 * Batch swaps resolve their receipt hash; all other transaction types use
 * `tx.hash` as their on-chain hash.
 */
export const getTxHash = (tx: Transaction): ChainHash | undefined => {
  if (tx.transactionType === 'intents') {
    const raw = tx.raw as IntentsStatusResponse | IntentsSubmitReceipt | undefined;
    const status = raw && 'status' in raw ? raw.status : undefined;
    if (status === 'cancelled') return undefined;

    const txHash = raw && 'txHash' in raw ? raw.txHash : undefined;
    return txHash && txHash !== tx.hash ? (txHash as ChainHash) : undefined;
  }

  if (tx.transactionType === 'gasless') {
    const raw = tx.raw as GaslessStatusResponse | undefined;
    return (raw?.transactions?.[0]?.hash as ChainHash) ?? undefined;
  }

  if (tx.transactionType === 'batch-swap') {
    return getBatchSwapOnChainHash(tx.raw);
  }

  return tx.hash;
};

const isValidTx = (
  transaction: Transaction,
  completed: Transaction,
  txType: 'swap' | 'batch-swap' | 'wrap' | 'gasless' | 'intents' | 'solana-swap' | 'cross-chain',
) =>
  transaction.transactionType === txType &&
  completed.transactionType === txType &&
  !!completed.hash &&
  transaction.hash === completed.hash;

const isSwapTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (
    !isValidTx(transaction, completed, 'swap') &&
    !isValidTx(transaction, completed, 'batch-swap')
  )
    return false;
  const status = (completed.raw as SerializableTransactionReceipt)?.status;

  return status === 'success';
};

const isWrapTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'wrap')) return false;
  const status = (completed.raw as SerializableTransactionReceipt)?.status;

  return status === 'success';
};

const isGaslessTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'gasless')) return false;
  const status = (completed.raw as GaslessStatusResponse)?.status;
  return status === 'succeeded' || status === 'confirmed';
};

const isIntentsTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'intents')) return false;
  const status = (completed.raw as IntentsStatusResponse)?.status;
  return status === 'succeeded' || status === 'confirmed';
};

const isSolanaTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'solana-swap')) return false;
  return (completed as SolanaSwapTransaction).status === 'confirmed';
};

const isCrossChainTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'cross-chain')) return false;
  const status = (completed.raw as CrossChainStatusReceipt)?.status;
  return status === 'success';
};

export const isTransactionCompletedSuccessfully = (
  transaction: Transaction,
  completed: Transaction,
) =>
  isWrapTransactionCompletedSuccessfully(transaction, completed) ||
  isSwapTransactionCompletedSuccessfully(transaction, completed) ||
  isGaslessTransactionCompletedSuccessfully(transaction, completed) ||
  isIntentsTransactionCompletedSuccessfully(transaction, completed) ||
  isSolanaTransactionCompletedSuccessfully(transaction, completed) ||
  isCrossChainTransactionCompletedSuccessfully(transaction, completed);

const isSwapTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (
    !isValidTx(transaction, completed, 'swap') &&
    !isValidTx(transaction, completed, 'batch-swap')
  )
    return false;
  if (isNotObservedReceipt(completed.raw)) return true;
  if (isReplacedReceipt(completed.raw)) return true;
  const status = (completed.raw as SerializableTransactionReceipt)?.status;
  return status === 'reverted';
};

const isWrapTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'wrap')) return false;
  if (isNotObservedReceipt(completed.raw)) return true;
  if (isReplacedReceipt(completed.raw)) return true;
  const status = (completed.raw as SerializableTransactionReceipt)?.status;
  return status === 'reverted';
};

const isGaslessTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'gasless')) return false;
  const status = (completed.raw as GaslessStatusResponse)?.status;
  return status === 'failed';
};

const isIntentsTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'intents')) return false;
  const status = (completed.raw as IntentsStatusResponse)?.status;
  return status === 'failed';
};

const isSolanaTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'solana-swap')) return false;
  return (completed as SolanaSwapTransaction).status === 'failed';
};

const isCrossChainTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) => {
  if (!isValidTx(transaction, completed, 'cross-chain')) return false;
  const status = (completed.raw as CrossChainStatusReceipt)?.status;
  return status === 'reverted';
};

export const isTransactionCompletedWithFailure = (
  transaction: Transaction,
  completed: Transaction,
) =>
  isSwapTransactionCompletedWithFailure(transaction, completed) ||
  isWrapTransactionCompletedWithFailure(transaction, completed) ||
  isGaslessTransactionCompletedWithFailure(transaction, completed) ||
  isIntentsTransactionCompletedWithFailure(transaction, completed) ||
  isSolanaTransactionCompletedWithFailure(transaction, completed) ||
  isCrossChainTransactionCompletedWithFailure(transaction, completed);

const isSwapTransactionPending = (transaction: Transaction): boolean => {
  if (transaction.transactionType !== 'swap' && transaction.transactionType !== 'batch-swap')
    return false;
  if (isNotObservedReceipt(transaction.raw)) return false;
  return !(transaction.raw && 'status' in transaction.raw);
};

const isWrapTransactionPending = (transaction: Transaction): boolean => {
  if (transaction.transactionType !== 'wrap') return false;
  if (isNotObservedReceipt(transaction.raw)) return false;
  return !(transaction.raw && 'status' in transaction.raw);
};

const isGaslessTransactionPending = (transaction: Transaction): boolean => {
  const status = transaction.raw && (transaction.raw as GaslessStatusResponse)?.status;

  return transaction.transactionType === 'gasless'
    ? !status || status === 'pending' || status === 'submitted'
    : false;
};

const isIntentsTransactionPending = (transaction: Transaction): boolean => {
  const status = transaction.raw && (transaction.raw as IntentsStatusResponse)?.status;

  // Pending unless a known-terminal status: a status outside the client's
  // vocabulary (warned by /api/intents/status) keeps IntentsWatcher polling
  // instead of stranding the trade in a never-resolving state. `cancelling`
  // stays pending deliberately - the cancellation tx is in flight and the
  // trade can still settle or cancel.
  return transaction.transactionType === 'intents'
    ? !(
        status === 'succeeded' ||
        status === 'confirmed' ||
        status === 'failed' ||
        status === 'cancelled'
      )
    : false;
};

const isCrossChainTransactionPending = (transaction: Transaction): boolean =>
  !!(
    transaction.transactionType === 'cross-chain' &&
    !(transaction.raw && 'status' in transaction.raw)
  );

const isSolanaTransactionPending = (transaction: Transaction): boolean =>
  transaction.transactionType === 'solana-swap' &&
  !!transaction.hash &&
  !(transaction as SolanaSwapTransaction).status;

export const isTransactionPending = (transaction: Transaction) => {
  // Note: For Solana, we check if it has a hash but no confirmed/failed status yet
  // The SolanaTransactionUpdater component polls for confirmation status
  return (
    isWrapTransactionPending(transaction) ||
    isSwapTransactionPending(transaction) ||
    isGaslessTransactionPending(transaction) ||
    isIntentsTransactionPending(transaction) ||
    isCrossChainTransactionPending(transaction) ||
    isSolanaTransactionPending(transaction)
  );
};

const isGaslessTransactionSuccessful = (transaction: GaslessTransaction): boolean => {
  const status = transaction.raw && (transaction.raw as GaslessStatusResponse)?.status;
  return status === 'confirmed' || status === 'succeeded';
};

const isIntentsTransactionSuccessful = (transaction: IntentsTransaction): boolean => {
  const status = transaction.raw && (transaction.raw as IntentsStatusResponse)?.status;
  return status === 'confirmed' || status === 'succeeded';
};

const isSwapOrWrapTransactionSuccessful = (
  transaction: SwapTransaction | WrapTransaction | BatchSwapTransaction,
): boolean => {
  const status = transaction.raw && (transaction.raw as SerializableTransactionReceipt)?.status;
  return status === 'success';
};

const isSolanaTransactionSuccessful = (transaction: SolanaSwapTransaction): boolean => {
  return transaction.status === 'confirmed';
};

const isCrossChainTransactionSuccessful = (transaction: CrossChainTransaction): boolean => {
  const status = transaction.raw && (transaction.raw as CrossChainStatusReceipt)?.status;
  return status === 'success';
};

export const isTransactionSuccessful = (transaction: Transaction): boolean => {
  const { transactionType } = transaction;
  if (transactionType === 'swap' || transactionType === 'batch-swap' || transactionType === 'wrap')
    return isSwapOrWrapTransactionSuccessful(transaction);
  if (transactionType === 'gasless') return isGaslessTransactionSuccessful(transaction);
  if (transactionType === 'intents') return isIntentsTransactionSuccessful(transaction);
  if (transactionType === 'solana-swap')
    return isSolanaTransactionSuccessful(transaction as SolanaSwapTransaction);
  if (transactionType === 'cross-chain')
    return isCrossChainTransactionSuccessful(transaction as CrossChainTransaction);
  return false;
};
